import { isRecord } from '../utils/backendData';

export const FUND_TRANSFER_MARKETS = ['futures', 'fund', 'spot'] as const;

export type FundTransferMarket = typeof FUND_TRANSFER_MARKETS[number];

export type TransferFundsRequest = {
  profileId: string;
  fromExchangeClientId: string;
  toExchangeClientId: string;
  amount: number;
};

export type TransferMarketFundsRequest = {
  profileId: string;
  exchangeClientId: string;
  fromMarket: FundTransferMarket;
  toMarket: FundTransferMarket;
  amount: number;
};

export type TransferFundsSuccessResponse = {
  success: true;
  tranId?: string;
};

export type TransferFundsFailureResponse = {
  success: false;
  message: string;
};

export type TransferFundsResponse = TransferFundsSuccessResponse | TransferFundsFailureResponse;

export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; message: string };

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizePositiveAmount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }

  return value;
}

export function isFundTransferMarket(value: unknown): value is FundTransferMarket {
  return typeof value === 'string' && FUND_TRANSFER_MARKETS.includes(value as FundTransferMarket);
}

export function validateTransferFundsRequest(value: unknown): ValidationResult<TransferFundsRequest> {
  if (!isRecord(value)) {
    return { success: false, message: 'Request body must be an object.' };
  }

  const profileId = value.profileId;
  const fromExchangeClientId = value.fromExchangeClientId;
  const toExchangeClientId = value.toExchangeClientId;
  const amount = normalizePositiveAmount(value.amount);

  if (!isNonEmptyString(profileId)) {
    return { success: false, message: 'profileId must be a non-empty string.' };
  }

  if (!isNonEmptyString(fromExchangeClientId)) {
    return { success: false, message: 'fromExchangeClientId must be a non-empty string.' };
  }

  if (!isNonEmptyString(toExchangeClientId)) {
    return { success: false, message: 'toExchangeClientId must be a non-empty string.' };
  }

  if (fromExchangeClientId === toExchangeClientId) {
    return { success: false, message: 'fromExchangeClientId and toExchangeClientId must be different.' };
  }

  if (amount === null) {
    return { success: false, message: 'amount must be a positive number.' };
  }

  return {
    success: true,
    data: {
      profileId: profileId.trim(),
      fromExchangeClientId: fromExchangeClientId.trim(),
      toExchangeClientId: toExchangeClientId.trim(),
      amount,
    },
  };
}

export function validateTransferMarketFundsRequest(
  value: unknown
): ValidationResult<TransferMarketFundsRequest> {
  if (!isRecord(value)) {
    return { success: false, message: 'Request body must be an object.' };
  }

  const profileId = value.profileId;
  const exchangeClientId = value.exchangeClientId;
  const fromMarket = value.fromMarket;
  const toMarket = value.toMarket;
  const amount = normalizePositiveAmount(value.amount);

  if (!isNonEmptyString(profileId)) {
    return { success: false, message: 'profileId must be a non-empty string.' };
  }

  if (!isNonEmptyString(exchangeClientId)) {
    return { success: false, message: 'exchangeClientId must be a non-empty string.' };
  }

  if (!isFundTransferMarket(fromMarket)) {
    return { success: false, message: 'fromMarket must be one of: futures, fund, spot.' };
  }

  if (!isFundTransferMarket(toMarket)) {
    return { success: false, message: 'toMarket must be one of: futures, fund, spot.' };
  }

  if (fromMarket === toMarket) {
    return { success: false, message: 'fromMarket and toMarket must be different.' };
  }

  if (amount === null) {
    return { success: false, message: 'amount must be a positive number.' };
  }

  return {
    success: true,
    data: {
      profileId: profileId.trim(),
      exchangeClientId: exchangeClientId.trim(),
      fromMarket,
      toMarket,
      amount,
    },
  };
}
